import Vue, { reactive } from 'vue'
import adminUserApi from '@/api/adminUser'
import store from '@/store/index'
import { replaceUserInList } from '@/utils/userUtils'

const buildArrivalTimeOptions = (startHour, endHour, stepMinutes) => {
  const options = []
  const start = new Date()
  start.setHours(startHour, 0, 0, 0)
  const end = new Date()
  end.setHours(endHour, 0, 0, 0)

  while (start.getTime() <= end.getTime()) {
    const timeHMS = start.toTimeString().slice(0, 8)
    const timeHM = start.toTimeString().slice(0, 5)
    options.push({ val: timeHMS, label: timeHM })
    start.setMinutes(start.getMinutes() + stepMinutes)
  }

  return options
}

export const useDispatchPlanSetting = () => {
  const state = reactive({
    arrivalTimeOptions: [],
    pickupCountOptions: [],
    taskDurationMinutesOptions: [],
  })

  const initializeDispatchPlanOptions = () => {
    state.arrivalTimeOptions = buildArrivalTimeOptions(12, 22, 30)
    state.pickupCountOptions = Array.from({ length: 15 }, (_, i) => i + 1)
    state.taskDurationMinutesOptions = [
      { val: 0, label: '0:00' },
      { val: 1, label: '0:01' },
      { val: 3, label: '0:03' },
      { val: 5, label: '0:05' },
      { val: 7, label: '0:07' },
      { val: 10, label: '0:10' },
      { val: 20, label: '0:20' },
      { val: 30, label: '0:30' },
    ]
  }

  const canEditDispatchPlanSetting = () => {
    return store.getters['user/isStaffTypeManagerOrLeader']
  }

  const formatIsDispatchExcludedForDisplay = (isDispatchExcluded) => {
    return isDispatchExcluded ? 'なし' : 'あり'
  }

  const formatTimeForDisplay = (value) => {
    if (value === null || value === undefined || value === '') {
      return ''
    }
    if (typeof value === 'number') {
      const hour = Math.floor(value / 60)
      const minute = value % 60
      return `${hour}:${String(minute).padStart(2, '0')}`
    }
    if (typeof value === 'string') {
      if (/^\d+$/.test(value)) {
        const parsed = Number(value)
        if (!Number.isNaN(parsed)) {
          const hour = Math.floor(parsed / 60)
          const minute = parsed % 60
          return `${hour}:${String(minute).padStart(2, '0')}`
        }
      }
      return value.slice(0, 5)
    }
    return value
  }

  const initializeDispatchPlanInputState = (user) => {
    Vue.set(user, 'input_arrival_start_time', false)
    Vue.set(user, 'input_arrival_start_time_str', null)
    Vue.set(user, 'input_arrival_deadline_time', false)
    Vue.set(user, 'input_arrival_deadline_time_str', null)
    Vue.set(user, 'input_pickup_count', false)
    Vue.set(user, 'input_pickup_count_str', null)
    Vue.set(user, 'input_task_duration_minutes', false)
    Vue.set(user, 'input_task_duration_minutes_str', null)
    Vue.set(user, 'input_is_dispatch_excluded', false)
    Vue.set(user, 'input_is_dispatch_excluded_val', null)
  }

  const switchToInputModeArrivalStartTime = (user) => {
    user.input_arrival_start_time = true
    user.input_arrival_start_time_str = user.arrival_start_time || ''
  }

  const switchToNonInputModeArrivalStartTime = (user) => {
    user.input_arrival_start_time = false
    user.input_arrival_start_time_str = null
  }

  const switchToInputModeArrivalDeadlineTime = (user) => {
    user.input_arrival_deadline_time = true
    user.input_arrival_deadline_time_str = user.arrival_deadline_time || ''
  }

  const switchToNonInputModeArrivalDeadlineTime = (user) => {
    user.input_arrival_deadline_time = false
    user.input_arrival_deadline_time_str = null
  }

  const switchToInputModePickupCount = (user) => {
    user.input_pickup_count = true
    user.input_pickup_count_str = user.pickup_count || ''
  }

  const switchToNonInputModePickupCount = (user) => {
    user.input_pickup_count = false
    user.input_pickup_count_str = null
  }

  const switchToInputModeTaskDurationMinutes = (user) => {
    user.input_task_duration_minutes = true
    user.input_task_duration_minutes_str = user.task_duration_minutes ?? ''
  }

  const switchToNonInputModeTaskDurationMinutes = (user) => {
    user.input_task_duration_minutes = false
    user.input_task_duration_minutes_str = null
  }

  const updateArrivalStartTime = async(users, idx, user) => {
    const obj = {
      ['arrival_start_time']: user.input_arrival_start_time_str || null
    }
    const arrivalDeadlineTime = user.arrival_deadline_time || null
    if (!isValidArrivalTimeRange(obj.arrival_start_time, arrivalDeadlineTime)) {
      alert('集配開始は集配期限より後の時刻を指定できません。')
      return
    }
    try {
      await adminUserApi.updateDispatchPlanStartTime(user.id, obj)
      user.arrival_start_time = obj.arrival_start_time
      replaceUserInList(users, idx, user)
      switchToNonInputModeArrivalStartTime(user)
    } catch (error) {
      console.error(error)
    }
  }

  const updateArrivalDeadlineTime = async(users, idx, user) => {
    const obj = {
      ['arrival_deadline_time']: user.input_arrival_deadline_time_str || null
    }
    const arrivalStartTime = user.arrival_start_time || null
    if (!isValidArrivalTimeRange(arrivalStartTime, obj.arrival_deadline_time)) {
      alert('集配期限は集配開始より前の時刻を指定できません。')
      return
    }
    try {
      await adminUserApi.updateDispatchPlanDeadlineTime(user.id, obj)
      user.arrival_deadline_time = obj.arrival_deadline_time
      replaceUserInList(users, idx, user)
      switchToNonInputModeArrivalDeadlineTime(user)
    } catch (error) {
      console.error(error)
    }
  }

  const isValidArrivalTimeRange = (arrivalStartTime, arrivalDeadlineTime) => {
    if (!arrivalStartTime || !arrivalDeadlineTime) {
      return true
    }
    return arrivalStartTime <= arrivalDeadlineTime
  }

  const updatePickupCount = async(users, idx, user) => {
    const obj = {
      ['pickup_count']: user.input_pickup_count_str || null
    }
    try {
      await adminUserApi.updateDispatchPlanPickupCount(user.id, obj)
      user.pickup_count = obj.pickup_count
      replaceUserInList(users, idx, user)
      switchToNonInputModePickupCount(user)
    } catch (error) {
      console.error(error)
    }
  }

  const switchToInputModeIsDispatchExcluded = (user) => {
    user.input_is_dispatch_excluded = true
    user.input_is_dispatch_excluded_val = user.is_dispatch_excluded
  }

  const switchToNonInputModeIsDispatchExcluded = (user) => {
    user.input_is_dispatch_excluded = false
    user.input_is_dispatch_excluded_val = null
  }

  const updateTaskDurationMinutes = async(users, idx, user) => {
    const taskDurationMinutes = user.input_task_duration_minutes_str !== ''
      ? user.input_task_duration_minutes_str
      : null
    const obj = {
      ['task_duration_minutes']: taskDurationMinutes
    }
    try {
      await adminUserApi.updateDispatchPlanTaskDurationMinutes(user.id, obj)
      user.task_duration_minutes = obj.task_duration_minutes
      replaceUserInList(users, idx, user)
      switchToNonInputModeTaskDurationMinutes(user)
    } catch (error) {
      console.error(error)
    }
  }

  const updateIsDispatchExcluded = async(users, idx, user) => {
    const dispatchExcludedParams = { is_dispatch_excluded: user.input_is_dispatch_excluded_val }
    try {
      await adminUserApi.updateDispatchPlanIsDispatchExcluded(user.id, dispatchExcludedParams)
      user.is_dispatch_excluded = dispatchExcludedParams.is_dispatch_excluded
      replaceUserInList(users, idx, user)
      switchToNonInputModeIsDispatchExcluded(user)
    } catch (error) {
      console.error(error)
    }
  }

  return {
    state,
    initializeDispatchPlanOptions,
    canEditDispatchPlanSetting,
    formatIsDispatchExcludedForDisplay,
    formatTimeForDisplay,
    initializeDispatchPlanInputState,
    switchToInputModeArrivalStartTime,
    switchToNonInputModeArrivalStartTime,
    switchToInputModeArrivalDeadlineTime,
    switchToNonInputModeArrivalDeadlineTime,
    switchToInputModePickupCount,
    switchToNonInputModePickupCount,
    switchToInputModeTaskDurationMinutes,
    switchToNonInputModeTaskDurationMinutes,
    updateArrivalStartTime,
    updateArrivalDeadlineTime,
    updatePickupCount,
    updateTaskDurationMinutes,
    switchToInputModeIsDispatchExcluded,
    switchToNonInputModeIsDispatchExcluded,
    updateIsDispatchExcluded,
  }
}
